import { Colors } from '@client/style/Variables';
import React, { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { TextStyle } from '@client/style/TextStyles';

/**
 * Text
 */

const InputsContainer = styled.div`
	border-radius: 20px;
	background-color: ${Colors.defaultOffWhite};
	border: 2px solid;

	display: flex;
	gap: 10px;
	justify-content: center;
	align-items: center;

	${TextStyle.family('title')}
	${TextStyle.size('large')}
`;

const Input = styled.input`
	background: none;
	border: none;
	padding: 10px 15px;
	text-align: left;

	:focus {
		outline: none;
	}

	${TextStyle.family('title')}
	${TextStyle.size('large')}

	&:nth-child(1) {
		border-radius: 20px 0 0 20px;
	}
	&:nth-child(3) {
		border-radius: 0 20px 20px 0;
	}
`;

interface IProps {
	initialValue?: {
		serial?: string | null;
		control?: string | null;
	};
	onChange: (data: { valid: boolean; value: { serial: string; control: string } }) => void;
	disabled?: boolean;
}

const serialRegex = /^\d{0,6}$/;
const controlRegex = /^[A-Z]{0,4}$/;

export const RegistrationInput = (props: IProps) => {
	const serialInputRef = useRef<HTMLInputElement | null>(null);
	const controlInputRef = useRef<HTMLInputElement | null>(null);

	const [values, setValues] = useState({
		serial: props.initialValue?.serial ?? '',
		control: props.initialValue?.control ?? '',
	});

	const onInputChange = useCallback(
		(ev: ChangeEvent<HTMLInputElement>) => {
			const val = ev.target.value.trim().toUpperCase();
			const id = ev.target.id;

			if (id === 'serial' && val.length === 6 && window.document.activeElement === ev.currentTarget) {
				controlInputRef.current?.focus();
			}

			if ((id === 'serial' && !serialRegex.test(val)) || (id === 'control' && !controlRegex.test(val))) {
				ev.preventDefault();
				return;
			}

			setValues(curr => ({
				...curr,
				[id]: val,
			}));
		},
		[setValues, controlInputRef.current]
	);

	useEffect(() => {
		props.onChange({
			value: values,
			valid:
				values.control.length >= 4 &&
				values.serial.length >= 6 &&
				(values.control !== props.initialValue?.control || values.serial !== props.initialValue?.serial),
		});
	}, [values, props.initialValue, props.onChange]);

	useEffect(() => {
		serialInputRef.current?.focus();
	}, [serialInputRef.current]);

	return (
		<InputsContainer>
			<Input
				ref={serialInputRef}
				disabled={props.disabled}
				onChange={onInputChange}
				value={values.serial}
				id='serial'
				type='text'
				inputMode='numeric'
				placeholder='000000'
				minLength={6}
				maxLength={6}
				required={true}
				size={6}
			/>
			<span>-</span>
			<Input
				ref={controlInputRef}
				disabled={props.disabled}
				onChange={onInputChange}
				value={values.control}
				id='control'
				type='text'
				placeholder='AAAA'
				minLength={4}
				maxLength={4}
				required={true}
				size={5}
			/>
		</InputsContainer>
	);
};
